// @flow

import {
  type IncomingPayment,
  type OpenedIncomingPayment,
  getCustomerEmail,
  getCustomerName,
  getDocument,
  getPhoneNumber,
  getReceipts,
  getPaymentType,
  getPaymentTypeCode,
  getCountry,
  getPersonType,
  getCheckoutPaymentOptions,
  getCheckoutPaymentOptionsV2,
} from '@tectonics/prs/incoming-payment';
import { getEWalletCheckoutPaymentOptions } from '@tectonics/prs/incoming-payment/checkout-payment-options';
import { filterNigerianEwalletBrands } from '@tectonics/prs/nigeria/ewallet-brands';

import { getAddress } from '@tectonics/prs/address';
import { toNationalPhoneNumber } from '@tectonics/utils/formatting';
import { orEmpty } from '@tectonics/utils/getters';

import { getDocumentTypeInferredFromValue as getBrazilianDocumentTypeInferredFromValue } from '@tectonics/prs/brazil';
import { getLowerInstalmentKey } from '@tectonics/prs/receipts';
import type { UserInputValues } from '@tectonics/prs/user-input-values/types';
import { decideArgentineDocument, filterArgentineCashBankCode } from '@tectonics/prs/argentina';
import { peruvianDocumentTypeInferredFromValue } from '@tectonics/prs/peru/peruvian-document-type-support';
import { formatValueWithMask } from '@tectonics/components/input-mask';
import { TURKEY_PHONE_MASK } from '@tectonics/commons/country/countries/turkey';
import { MALAYSIA_PHONE_MASK } from '@tectonics/commons/country/countries/malaysia';

export function getOpenedPaymentBasedInitialUserInputValues(incomingPayment: OpenedIncomingPayment): UserInputValues {
  return {
    ...getOpenedCommonsInitialValues(incomingPayment),
    ...getCountryInitialValues(incomingPayment),
  };
}

function getOpenedCommonsInitialValues(incomingPayment: OpenedIncomingPayment): UserInputValues {
  return {
    selectedPaymentMethod: '',
    customerName: getCustomerName(incomingPayment),
    customerEmail: getCustomerEmail(incomingPayment) || '',
    cardHolderName: '',
    cardNumber: '',
    cardExpiryDate: '',
    cardSecurityCode: '',
    selectedInstalment: getLowerInstalmentKey(getReceipts(incomingPayment)),
    acceptVisaTerms: true,
    selectedBankCode: '',
    selectedTefBank: '',
    selectedSafetypayOption: getInitialSafetypayOption(incomingPayment),
    selectedCryptocurrency: '',
    selectedProtocol: '',
  };
}

function getInitialSafetypayOption(incomingPayment: IncomingPayment): string {
  const paymentTypeCode = getPaymentTypeCode(incomingPayment);

  return (paymentTypeCode === 'safetypay-cash' || paymentTypeCode === 'safetypay-online')
    ? paymentTypeCode
    : '';
}

function getCountryInitialValues(incomingPayment: OpenedIncomingPayment): UserInputValues {
  const address = getAddress(incomingPayment);
  const maskValue = function(value: string, country: string): string {
    switch (country) {
      case 'ke':
        return `+254 ${value.substring(3)}`;
      case 'gh':
        return `+233 ${value.substring(3)}`;
      case 'ug':
        return `+256 ${value.substring(3)}`;
      case 'ng':
        return `+234 ${value.substring(3)}`;
      case 'tr':
        return formatValueWithMask(`+90 ${value.substring(2)}`, TURKEY_PHONE_MASK);
      case 'my':
        return formatValueWithMask(`+60 ${value.replace(/^\+?60/, '')}`, MALAYSIA_PHONE_MASK);

      default:
        return '';
    }
  };

  switch (getCountry(incomingPayment)) {
    case 'br':
      return {
        brazilianDocument: getDocumentOrEmpty(incomingPayment),
        brazilianDocumentType: getBrazilianDocumentTypeInferredFromValue(getDocument(incomingPayment)),
        brazilianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        brazilianZipcode: orEmpty(address.zipcode),
        brazilianState: orEmpty(address.state),
        brazilianCity: orEmpty(address.city),
        brazilianAddress: orEmpty(address.street_name),
        brazilianStreetNumber: orEmpty(address.street_number),
        brazilianStreetComplement: orEmpty(address.street_complement),
        selectedEWallet: '',
        mealVoucherVrPreTokenizedCardCvv: '',
      };

    case 'mx':
      return {
        mexicanPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        mexicanZipcode: orEmpty(address.zipcode),
        mexicanState: orEmpty(address.state),
        mexicanCity: orEmpty(address.city),
        mexicanAddress: orEmpty(address.street_name),
        mexicanStreetNumber: orEmpty(address.street_number),
        mexicanStreetComplement: orEmpty(address.street_complement),
        selectedEWallet: 'mercadopago',
      };

    case 'ar': {
      const argentineDocument = getDocumentOrEmpty(incomingPayment);

      return {
        argentineDocument: argentineDocument,
        argentineCbu: '',
        companyName: '',
        argentineDocumentType: decideArgentineDocument(argentineDocument),
        argentinePhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        argentineZipcode: orEmpty(address.zipcode),
        argentineState: orEmpty(address.state),
        argentineCity: orEmpty(address.city),
        argentineAddress: orEmpty(address.street_name),
        argentineStreetNumber: orEmpty(address.street_number),
        argentineStreetComplement: orEmpty(address.street_complement),
        selectedBankCode: filterArgentineCashBankCode(getPaymentTypeCode(incomingPayment)),
        selectedEWallet: getSelectedEWallet(incomingPayment),
        acceptTerms: false,
      };
    }

    case 'co': {
      const personType = getPersonType(incomingPayment);

      return {
        colombianDocument: getDocumentOrEmpty(incomingPayment),
        colombianDocumentType: personType === 'business' ? 'NIT' : 'CC',
        colombianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        colombianZipcode: orEmpty(address.zipcode),
        colombianState: orEmpty(address.state),
        colombianCity: orEmpty(address.city),
        colombianAddress: orEmpty(address.street_name),
        colombianStreetNumber: orEmpty(address.street_number),
        colombianStreetComplement: orEmpty(address.street_complement),
        selectedEftBank: '',
        selectedEWallet: 'nequi',
        selectedExternalPayProvider: getInitialColombianExternalPayProvider(incomingPayment),
        selectedExternalPayProviderBank: '',
      };
    }

    case 'cl':
      return {
        chileanDocumentType: 'RUT',
        chileanDocument: getDocumentOrEmpty(incomingPayment),
        chileanPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        chileanZipcode: orEmpty(address.zipcode),
        chileanState: orEmpty(address.state),
        chileanCity: orEmpty(address.city),
        chileanAddress: orEmpty(address.street_name),
        chileanStreetNumber: orEmpty(address.street_number),
        chileanStreetComplement: orEmpty(address.street_complement),
        selectedEWallet: getSelectedEWallet(incomingPayment),
      };

    case 'pe':
      return {
        peruvianDocumentType: peruvianDocumentTypeInferredFromValue(getDocumentOrEmpty(incomingPayment)),
        peruvianDocument: getDocumentOrEmpty(incomingPayment),
        peruvianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        peruvianZipcode: orEmpty(address.zipcode),
        peruvianState: orEmpty(address.state),
        peruvianCity: orEmpty(address.city),
        peruvianAddress: orEmpty(address.street_name),
        peruvianStreetNumber: orEmpty(address.street_number),
        peruvianStreetComplement: orEmpty(address.street_complement),
        selectedEWallet: 'yape',
      };

    case 'ec':
      return {
        ecuadorianDocument: getDocumentOrEmpty(incomingPayment),
        ecuadorianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        ecuadorianZipcode: orEmpty(address.zipcode),
        ecuadorianState: orEmpty(address.state),
        ecuadorianCity: orEmpty(address.city),
        ecuadorianAddress: orEmpty(address.street_name),
        ecuadorianStreetNumber: orEmpty(address.street_number),
        ecuadorianStreetComplement: orEmpty(address.street_complement),
      };

    case 'bo':
      return {
        bolivianDocument: getDocumentOrEmpty(incomingPayment),
        bolivianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        bolivianZipcode: orEmpty(address.zipcode),
        bolivianState: orEmpty(address.state),
        bolivianCity: orEmpty(address.city),
        bolivianAddress: orEmpty(address.street_name),
        bolivianStreetNumber: orEmpty(address.street_number),
        bolivianStreetComplement: orEmpty(address.street_complement),
      };

    case 'uy': {
      return {
        uruguayanDocumentType: 'CI',
        uruguayanDocument: getDocumentOrEmpty(incomingPayment),
        uruguayanPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        uruguayanZipcode: orEmpty(address.zipcode),
        uruguayanState: orEmpty(address.state),
        uruguayanCity: orEmpty(address.city),
        uruguayanAddress: orEmpty(address.street_name),
        uruguayanStreetNumber: orEmpty(address.street_number),
        uruguayanStreetComplement: orEmpty(address.street_complement),
        selectedEWallet: getSelectedEWallet(incomingPayment),
      };
    }

    case 'cr': {
      return {
        costaRicanDocument: getDocumentOrEmpty(incomingPayment),
        costaRicanPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        costaRicanZipcode: orEmpty(address.zipcode),
        costaRicanState: orEmpty(address.state),
        costaRicanCity: orEmpty(address.city),
        costaRicanAddress: orEmpty(address.street_name),
        costaRicanStreetNumber: orEmpty(address.street_number),
        costaRicanStreetComplement: orEmpty(address.street_complement),
      };
    }

    case 'py': {
      return {
        paraguayanDocument: getDocumentOrEmpty(incomingPayment),
        paraguayanPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        paraguayanZipcode: orEmpty(address.zipcode),
        paraguayanState: orEmpty(address.state),
        paraguayanCity: orEmpty(address.city),
        paraguayanAddress: orEmpty(address.street_name),
        paraguayanStreetNumber: orEmpty(address.street_number),
        paraguayanStreetComplement: orEmpty(address.street_complement),
      };
    }

    case 'pa': {
      return {
        panamanianDocument: getDocumentOrEmpty(incomingPayment),
        panamanianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        panamanianZipcode: orEmpty(address.zipcode),
        panamanianState: orEmpty(address.state),
        panamanianCity: orEmpty(address.city),
        panamanianAddress: orEmpty(address.street_name),
        panamanianStreetNumber: orEmpty(address.street_number),
        panamanianStreetComplement: orEmpty(address.street_complement),
      };
    }

    case 'sv': {
      return {
        elSalvadorDocument: getDocumentOrEmpty(incomingPayment),
        elSalvadorPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        elSalvadorZipcode: orEmpty(address.zipcode),
        elSalvadorState: orEmpty(address.state),
        elSalvadorCity: orEmpty(address.city),
        elSalvadorAddress: orEmpty(address.street_name),
        elSalvadorStreetNumber: orEmpty(address.street_number),
        elSalvadorStreetComplement: orEmpty(address.street_complement),
      };
    }

    case 'gt': {
      return {
        guatemalanDocument: getDocumentOrEmpty(incomingPayment),
        guatemalanPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        guatemalanZipcode: orEmpty(address.zipcode),
        guatemalanState: orEmpty(address.state),
        guatemalanCity: orEmpty(address.city),
        guatemalanAddress: orEmpty(address.street_name),
        guatemalanStreetNumber: orEmpty(address.street_number),
        guatemalanStreetComplement: orEmpty(address.street_complement),
      };
    }

    case 'do': {
      return {
        dominicanDocument: getDocumentOrEmpty(incomingPayment),
        dominicanPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        dominicanZipcode: orEmpty(address.zipcode),
        dominicanState: orEmpty(address.state),
        dominicanCity: orEmpty(address.city),
        dominicanAddress: orEmpty(address.street_name),
        dominicanStreetNumber: orEmpty(address.street_number),
        dominicanStreetComplement: orEmpty(address.street_complement),
      };
    }

    case 'za': {
      return {
        southAfricanDocument: getDocumentOrEmpty(incomingPayment),
        southAfricanPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        southAfricanZipcode: orEmpty(address.zipcode),
        southAfricanState: orEmpty(address.state),
        southAfricanCity: orEmpty(address.city),
        southAfricanAddress: orEmpty(address.street_name),
        southAfricanStreetNumber: orEmpty(address.street_number),
        southAfricanStreetComplement: orEmpty(address.street_complement),
        selectedEWallet: 'capitec-pay',
      };
    }

    case 'ke': {
      return {
        kenyanDocument: getDocumentOrEmpty(incomingPayment),
        kenyanPhoneNumber: maskValue(getPhoneNumberOrEmpty(incomingPayment), 'ke'),
        kenyanZipcode: orEmpty(address.zipcode),
        kenyanState: orEmpty(address.state),
        kenyanCity: orEmpty(address.city),
        kenyanAddress: orEmpty(address.street_name),
        kenyanStreetNumber: orEmpty(address.street_number),
        kenyanStreetComplement: orEmpty(address.street_complement),
        selectedEWallet: 'mpesa',
      };
    }

    case 'ng': {
      return {
        nigerianPhoneNumber: maskValue(getPhoneNumberOrEmpty(incomingPayment), 'ng'),
        nigerianAccountNumber: '',
        nigerianAddress: orEmpty(address.street_name),
        selectedEWallet: getInitialNigerianEWallet(incomingPayment),
      };
    }

    case 'in': {
      return {
        indiaCustomerName: getCustomerName(incomingPayment),
        indiaPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        indiaZipcode: orEmpty(address.zipcode),
        indiaState: orEmpty(address.state),
        indiaCity: orEmpty(address.city),
        indiaStreetNumber: orEmpty(address.street_number),
        indiaStreetComplement: orEmpty(address.street_complement),
        indiaAddress: orEmpty(address.street_name),
        indiaUpiId: '',
        indiaUpiApp: '',
        selectedEWallet: 'upi-qrcode',
      };
    }

    case 'ph': {
      return {
        philippinesPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        selectedEWallet: getPaymentTypeCode(incomingPayment),
      };
    }

    case 'tz': {
      return {
        tanzanianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        selectedEWallet: 'airtel',
      };
    }

    case 'ug': {
      return {
        ugandaPhoneNumber: maskValue(getPhoneNumberOrEmpty(incomingPayment), 'ug'),
        selectedEWallet: 'airtel',
      };
    }

    case 'zm': {
      return {
        name: getCustomerName(incomingPayment),
        email: getCustomerEmail(incomingPayment) || '',
        phone_number: toNationalPhoneNumber('zm', getPhoneNumberOrEmpty(incomingPayment)),
        selectedEWallet: 'airtel',
      };
    }

    case 'ci':
    case 'sn': {
      return {
        email: getCustomerEmail(incomingPayment) || '',
        phone_number: toNationalPhoneNumber(incomingPayment.country, getPhoneNumberOrEmpty(incomingPayment)),
        selectedEWallet: '',
        selectedMobileMoneyNetwork: '',
      };
    }

    case 'eg': {
      return {
        egyptPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
      };
    }

    case 'jm': {
      return {
        jamaicaPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
      };
    }

    case 'gh': {
      return {
        ghanaPhoneNumber: maskValue(getPhoneNumberOrEmptyGhana(incomingPayment) || '', 'gh'),
        selectedEWallet: 'airtel',
      };
    }

    case 'tr': {
      return {
        turkeyPhoneNumber: maskValue(getPhoneNumberOrEmpty(incomingPayment), 'tr'),
      };
    }

    case 'th': {
      return {
        thaiPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        selectedEWallet: '',
        selectedBankCode: '',
      };
    }

    case 'id': {
      return {
        indonesianPhoneNumber: getPhoneNumberOrEmpty(incomingPayment),
        selectedVirtualAccountBank: '',
        selectedEWallet: '',
      };
    }

    case 'my': {
      return {
        malaysiaPhoneNumber: maskValue(getPhoneNumberOrEmpty(incomingPayment), 'my'),
        selectedEWallet: getPaymentTypeCode(incomingPayment),
      };
    }

    default:
      throw new Error(`Country ${getCountry(incomingPayment)} not supported`);
  }
}

function getDocumentOrEmpty(incomingPayment: IncomingPayment): string {
  return getDocument(incomingPayment) || '';
}

function getPhoneNumberOrEmpty(incomingPayment: IncomingPayment): string {
  return getPhoneNumber(incomingPayment) || '';
}

function getPhoneNumberOrEmptyGhana(incomingPayment): ?string {
  return (!incomingPayment.hasOwnProperty('status_otp')) ? getPhoneNumber(incomingPayment) : '';
}

function getSelectedEWallet(incomingPayment: IncomingPayment) {
  const paymentTypeCode = getPaymentTypeCode(incomingPayment);

  switch (getCountry(incomingPayment)) {
    case 'ar':
      return paymentTypeCode === 'mpconnect' ? paymentTypeCode : 'mercadopago';
    case 'cl':
      return paymentTypeCode === 'mpconnect' ? paymentTypeCode : 'machpay';
    case 'uy':
      return paymentTypeCode === 'mpconnect' ? paymentTypeCode : 'mercadopago';
    default:
      return undefined;
  }
}

function getInitialNigerianEWallet(incomingPayment: OpenedIncomingPayment): string {
  const checkoutPaymentOptions = getCheckoutPaymentOptionsV2(incomingPayment) ?? getCheckoutPaymentOptions(incomingPayment);
  const allowedBrands = checkoutPaymentOptions ? getEWalletCheckoutPaymentOptions(checkoutPaymentOptions) : [];
  const brands = filterNigerianEwalletBrands(allowedBrands);

  return brands.length > 0 ? brands[0] : '';
}

function getInitialColombianExternalPayProvider(incomingPayment: IncomingPayment): string {
  const paymentTypeCode = getPaymentTypeCode(incomingPayment);

  return (paymentTypeCode === 'achpse' || paymentTypeCode === 'bancolombia-button')
    ? paymentTypeCode
    : '';
}

export function getPendingPaymentBasedInitialUserInputValues(incomingPayment: IncomingPayment): UserInputValues {
  return {
    selectedPaymentMethod: getPaymentType(incomingPayment),
  };
}

export function getConfirmedPaymentBasedInitialUserInputValues(incomingPayment: IncomingPayment): UserInputValues {
  return {
    selectedPaymentMethod: getPaymentType(incomingPayment),
  };
}

export function getCanceledPaymentBasedInitialUserInputValues(incomingPayment: IncomingPayment): UserInputValues {
  return {
    selectedPaymentMethod: getPaymentType(incomingPayment),
    selectedInstalment: getLowerInstalmentKey(getReceipts(incomingPayment)),
    eligible: incomingPayment['eligible'],
  };
}


export function getPaymentBasedInitialUserInputValues(incomingPayment: IncomingPayment): UserInputValues {
  switch (incomingPayment.status) {
    case 'OP':
      return getOpenedPaymentBasedInitialUserInputValues(incomingPayment);

    case 'PE':
      return getPendingPaymentBasedInitialUserInputValues(incomingPayment);

    case 'CO':
      return getConfirmedPaymentBasedInitialUserInputValues(incomingPayment);

    case 'CA':
      return getCanceledPaymentBasedInitialUserInputValues(incomingPayment);

    default:
      return {};
  }
}
